// The selected commit (spec/dashboard.md section 12.1): `GET …/commits/{sha}?include=content` — the message in full, the
// author and time, the parents as links, the changes grid; Revert…, Cherry-pick to…, Create branch from here.
import { useState } from 'react';
import { Link } from 'react-router';
import { ops } from '../../api/operations';
import type { CommitDetail as CommitDetailData } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { ChangesGrid } from '../../history/ChangesGrid';
import type { ContentChange } from '../../history/changes';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { formatInstant, useTimeZone } from '../../ui/time';
import { useQuery } from '../../workspace/cache';
import { CherryPickDialog, RevertDialog } from './RevertCherryPick';
import { PROTECTED_TEXT, errorText, shortSha } from './util';
import { SkeletonRows } from '../../ui/PageStates';
import { AlertCircle, CornerDownRight, GitBranch, GitCommitHorizontal, Undo2, X } from '../../ui/icons';

export function CommitDetail({ branch, sha, onCreateBranch, onClose }: { branch: string; sha: string; onCreateBranch: (sha: string) => void; onClose: () => void }) {
  const { client } = useAuth();
  const { org, game, branches, missing, offsets } = useGame();
  const [zone] = useTimeZone();
  const [dialog, setDialog] = useState<'revert' | 'cherry-pick' | null>(null);
  const q = useQuery(`branch:${game.id}:${branch}:history:commit:${sha}`, () => client.data(ops.getCommit, { path: { org, game: game.slug, branch, sha }, query: { include: 'content' } }) as unknown as Promise<CommitDetailData>);
  const isProtected = branches.find((b) => b.name === branch)?.isProtected ?? false;
  const detail = q.data;
  return (
    <section className="commit-detail pane" data-testid="commit-detail" aria-label={`Commit ${shortSha(sha)}`}>
      <div className="pane-head commit-detail-head">
        <GitCommitHorizontal className="icon" aria-hidden="true" />
        <code title={sha}>{shortSha(sha)}</code>
        {detail && detail.commit.parents.length > 1 && <Badge tone="info">merge</Badge>}
        <span className="spacer" />
        <Button size="s" variant="ghost" icon={<X aria-hidden="true" />} onClick={onClose} aria-label="Close the commit">
          Close
        </Button>
      </div>
      <div className="pane-body commit-detail-body">
      {q.error ? (
        <div className="banner banner-danger" role="alert">
          <AlertCircle className="banner-icon" aria-hidden="true" />
          <div className="banner-body">{errorText(q.error)}</div>
          <Button size="s" onClick={q.refetch}>
            Retry
          </Button>
        </div>
      ) : !detail ? (
        <SkeletonRows rows={4} label="Loading the commit" />
      ) : (
        <>
          <pre className="commit-message" data-testid="commit-message">
            {detail.commit.message}
          </pre>
          <dl className="commit-meta">
            <dt>Author</dt>
            <dd>
              {detail.commit.authorName} <span className="muted small">&lt;{detail.commit.authorEmail}&gt;</span>
            </dd>
            <dt>Time</dt>
            <dd>{formatInstant(detail.commit.authoredAt, zone, { offsets })}</dd>
            <dt>Parents</dt>
            <dd>
              {detail.commit.parents.length === 0 ? (
                <span className="muted">none (the first commit)</span>
              ) : (
                detail.commit.parents.map((p, i) => (
                  <span key={p}>
                    {i > 0 && ' · '}
                    <Link to={routes.history(org, game.slug, branch, p)} title={p}>
                      <code>{shortSha(p)}</code>
                    </Link>
                  </span>
                ))
              )}
            </dd>
          </dl>
          <div className="row commit-actions">
            <Button size="s" icon={<Undo2 aria-hidden="true" />} onClick={() => setDialog('revert')} missingPermission={missing('content_write')} disabled={isProtected} title={isProtected ? PROTECTED_TEXT : undefined}>
              Revert…
            </Button>
            <Button size="s" icon={<CornerDownRight aria-hidden="true" />} onClick={() => setDialog('cherry-pick')} missingPermission={missing('content_write')} disabled={branches.length < 2} title={branches.length < 2 ? 'The game has no other branch' : undefined}>
              Cherry-pick to…
            </Button>
            <Button size="s" icon={<GitBranch aria-hidden="true" />} onClick={() => onCreateBranch(sha)} missingPermission={missing('branch_create')}>
              Create branch from here
            </Button>
          </div>
          <ChangesGrid changes={detail.changes as ContentChange[]} branch={branch} contentTruncated={detail.contentTruncated ?? undefined} emptyMessage="This commit changes no file." />
          <RevertDialog open={dialog === 'revert'} onClose={() => setDialog(null)} commit={detail.commit} />
          <CherryPickDialog open={dialog === 'cherry-pick'} onClose={() => setDialog(null)} commit={detail.commit} />
        </>
      )}
      </div>
    </section>
  );
}
